// The theme owns the copy and CTA; the isolated frame owns the interactive clinic.
if (!customElements.get('nx-clinic-hero')) {
  customElements.define('nx-clinic-hero', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.frameUrl || this.controller) return;
      this.controller = new AbortController();
      this.cta = this.querySelector('[data-clinic-cta]');
      this.visual = this.querySelector('[data-clinic-visual]');
      if (!this.cta || !this.visual) return;
      this.originalLabel = this.cta.innerHTML;
      this.measureHero = () => {
        const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
        this.style.setProperty('--nx-hero-top', `${top}px`);
      };
      this.measureHero();
      const enableSnap = () => document.documentElement.classList.add('nx-clinic-scroll-ready');
      window.addEventListener('wheel', enableSnap, {once: true, passive: true, signal: this.controller.signal});
      window.addEventListener('touchmove', enableSnap, {once: true, passive: true, signal: this.controller.signal});
      window.addEventListener('resize', this.measureHero, {signal: this.controller.signal});
      this.layoutObserver = new ResizeObserver(this.measureHero);
      const header = document.querySelector('.section-header');
      if (header) this.layoutObserver.observe(header);
      const nextSection = this.closest('.shopify-section')?.nextElementSibling;
      if (nextSection) {this.nextSection=nextSection;nextSection.classList.add('nx-clinic-next-section');}
      this.cta.setAttribute('aria-expanded', 'false');
      this.cta.addEventListener('click', event => {
        if (this.failed) return; // The original explanatory-page link remains a fallback.
        event.preventDefault();
        this.pendingStart = true;
        if (this.ready) this.expand();
        else {
          this.cta.setAttribute('aria-busy', 'true');
          this.cta.textContent = 'Opening clinic…';
          this.load();
        }
      }, {signal: this.controller.signal});
      window.addEventListener('message', event => {
        if (event.source !== this.frame?.contentWindow || event.origin !== location.origin) return;
        switch (event.data?.type) {
          case 'nx-clinic-ready':
            clearTimeout(this.timeout);
            this.ready = true;
            this.classList.add('nx-clinic-ready');
            this.restoreLabel();
            this.frame.removeAttribute('tabindex');
            this.frame.setAttribute('aria-hidden', 'false');
            this.send({type: 'nx-clinic-visibility', visible: this.visible !== false});
            if (this.pendingStart) this.expand();
            break;
          case 'nx-clinic-collapsed':
            this.classList.remove('nx-clinic-expanded');
            this.cta.setAttribute('aria-expanded', 'false');
            this.cta.focus({preventScroll: true});
            break;
          case 'nx-clinic-error': this.fail(); break;
        }
      }, {signal: this.controller.signal});
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.send({type: 'nx-clinic-visibility', visible: this.visible});
      });
      this.visibilityObserver.observe(this);
      // Keep the image until interaction for reduced-motion or data-saving visitors.
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData) {
        this.loadObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) { this.loadObserver.disconnect(); this.load(); }
        }, {rootMargin: '200px'});
        this.loadObserver.observe(this);
      }
    }
    restoreLabel() {
      this.cta.innerHTML = this.originalLabel;
      this.cta.removeAttribute('aria-busy');
    }
    send(message) { this.frame?.contentWindow?.postMessage(message, location.origin); }
    expand() {
      this.pendingStart = false;
      this.classList.add('nx-clinic-expanded');
      this.cta.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.send({type: 'nx-clinic-start'}));
    }
    async load() {
      if (this.loading || this.failed) return;
      this.loading = true;
      const signal = this.controller.signal;
      this.timeout = setTimeout(() => this.fail(), 20000);
      try {
        const url = new URL(this.dataset.frameUrl, location.href);
        const {createFrameDocument} = await import(url.href);
        if (!this.isConnected || this.failed || signal.aborted) return;
        const frame = document.createElement('iframe');
        frame.title = 'Interactive NxVET clinic walkthrough';
        frame.className = 'nx-clinic-frame';
        frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.setAttribute('scrolling', 'no');
        frame.srcdoc = createFrameDocument(new URL('.', url).href, location.origin);
        this.frame = frame;
        this.visual.append(frame);
      } catch { if (!signal.aborted) this.fail(); }
    }
    fail() {
      clearTimeout(this.timeout);
      const wasExpanded = this.classList.contains('nx-clinic-expanded');
      this.failed = true;
      this.ready = false;
      this.classList.remove('nx-clinic-ready', 'nx-clinic-expanded');
      this.frame?.remove();
      this.frame = null;
      this.restoreLabel();
      this.cta.setAttribute('aria-expanded', 'false');
      if (wasExpanded) this.cta.focus({preventScroll: true});
    }
    disconnectedCallback() {
      clearTimeout(this.timeout);
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.layoutObserver?.disconnect();
      this.nextSection?.classList.remove('nx-clinic-next-section');
      this.loadObserver?.disconnect();
      this.frame?.remove();
      this.frame = null;
      this.classList.remove('nx-clinic-ready', 'nx-clinic-expanded');
      if (this.cta) { this.restoreLabel(); this.cta.setAttribute('aria-expanded', 'false'); }
      this.controller = null;
      this.loading = this.ready = this.pendingStart = this.failed = false;
    }
  });
}
